import { useState } from 'react';
import { Modal, Typography, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import TwoFactorAuthForm from '../../components/auth/TwoFactorAuthForm';
import { useAuthService } from '../../services/authService';
import { notificationType } from '../../common/constants/notificationType';
import { apiErrorText } from '../../utils/apiErrorText';
import { menuActionText, spacing, textColor } from '../../theming';
import icon from '../../common/styles/Icon.module.scss';

/**
 * The Users list row action for a user with 2FA on. Disabled while the Super Admin has no 2FA of their own,
 * because the reset is confirmed with a code from their authenticator.
 */
export const resetTwoFactorMenuItem = ({ record, currentUser, onClick, t }) => {
    if (!record.isTwoFactorEnable || record.isDeleted) {
        return null;
    }
    const disabled = !currentUser?.isTwoFactorEnable;

    return {
        key: 'reset-2fa',
        disabled,
        title: disabled ? t('users:reset2fa.needsOwn2fa') : undefined,
        icon: <i className={`fi fi-rr-shield-cross ${icon.md}`} style={{ color: textColor.subtle }} />,
        label: <span style={{ ...menuActionText }}>{t('users:reset2fa.action')}</span>,
        onClick,
    };
};

/**
 * A Super Admin switches off the 2FA of a user who lost their authenticator, confirmed with a fresh code from the
 * admin's own app. The user is signed out everywhere and can set 2FA up again after signing in.
 */
export default function ResetTwoFactorModal({ user, onClose, onDone }) {
    const { t } = useTranslation(['users', 'settings', 'apiResponses']);
    const { resetTwoFactorAuth } = useAuthService();
    const [api, contextHolder] = notification.useNotification();
    const [loading, setLoading] = useState(false);

    const onFormSubmit = async ({ code }) => {
        setLoading(true);
        try {
            await resetTwoFactorAuth(user.id, code);
            onDone();
        } catch (error) {
            api[notificationType.ERROR]({ ...apiErrorText(t, error), placement: 'top' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open
            title={t('users:reset2fa.title', { email: user.email })}
            footer={null}
            onCancel={onClose}
            destroyOnClose
            // App.scss zeroes the padding of every modal; the app's padded dialogs use the same spacing
            styles={{ content: { padding: `${spacing[5]} ${spacing[4]}` } }}
        >
            {contextHolder}
            <Typography.Paragraph>{t('users:reset2fa.explanation')}</Typography.Paragraph>
            <TwoFactorAuthForm
                loading={loading}
                showCancel
                submitDisabled={false}
                onBackClick={onClose}
                onFormSubmit={onFormSubmit}
                t={t}
            />
        </Modal>
    );
}
